import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Modal } from '@telegram-apps/telegram-ui';
import { useAppRootContext } from '@telegram-apps/telegram-ui/dist/hooks/useAppRootContext';
import { post } from '../../helpers/api';
import styles from './CreateProxyModal.module.css';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
};

type Created = { username: string; password: string };

const WORDS = [
  'sky', 'star', 'moon', 'wind', 'rain', 'cloud', 'fire', 'river',
  'green', 'blue', 'swift', 'brave', 'lucky', 'happy', 'smart', 'cool',
  'ocean', 'storm', 'tiger', 'eagle', 'panda', 'maple', 'cedar', 'amber',
  'frost', 'ember', 'pixel', 'nova', 'orbit', 'sonic', 'vivid', 'zephyr',
];

const randomInt = (max: number) => Math.floor(Math.random() * max);

const randomUsername = () => {
  for (;;) {
    const count = Math.random() < 0.5 ? 1 : 2;
    let out = '';
    for (let i = 0; i < count; i++) {
      out += WORDS[randomInt(WORDS.length)];
    }
    if (out.length >= 5 && out.length <= 10) {
      return out;
    }
  }
};

const randomPassword = () => {
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let out = '';
  for (let i = 0; i < 10; i++) {
    out += chars[randomInt(chars.length)];
  }
  return out;
};

export const CreateProxyModal = ({ open, onOpenChange, onSuccess }: Props) => {
  const { portalContainer } = useAppRootContext();
  const [username, setUsername] = useState('');
  const [realname, setRealname] = useState('');
  const [password, setPassword] = useState('');
  const [telegram, setTelegram] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [created, setCreated] = useState<Created | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const toastTimer = useRef<number>();

  useEffect(() => {
    if (open) {
      setUsername('');
      setRealname('');
      setPassword('');
      setTelegram('');
      setSubmitting(false);
      setError(null);
      setCreated(null);
      setCopied(false);
      setConfirming(false);
    }
  }, [open]);

  useEffect(() => () => window.clearTimeout(toastTimer.current), []);

  const showToast = (text: string) => {
    window.clearTimeout(toastTimer.current);
    setToast(text);
    toastTimer.current = window.setTimeout(() => setToast(null), 2200);
  };

  const handleRandom = () => {
    const name = randomUsername();
    setUsername(name);
    setRealname(name);
    setPassword(randomPassword());
    setError(null);
  };

  const openConfirm = () => {
    if (!username.trim() || !realname.trim() || !password.trim()) {
      setError('账号、昵称、密码不能为空');
      return;
    }
    setError(null);
    setConfirming(true);
  };

  const handleSubmit = async () => {
    if (submitting) {
      return;
    }
    if (!username.trim() || !realname.trim() || !password.trim()) {
      setError('账号、昵称、密码不能为空');
      setConfirming(false);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const account = username.trim();
      const pwd = password.trim();
      await post<number>('/admin/tel/proxy/create', {
        username: account,
        realname: realname.trim(),
        password: pwd,
        telegram: telegram.trim() || undefined,
      });
      showToast('代理创建成功');
      setConfirming(false);
      setCreated({ username: account, password: pwd });
      onSuccess?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : '创建失败');
      setConfirming(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!created) {
      return;
    }
    const text = `账号：${created.username}\n密码：${created.password}`;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopied(true);
  };

  const handleClose = () => {
    if (created) {
      setCreated(null);
      setCopied(false);
    }
    onOpenChange(false);
  };

  return (
    <>
      <Modal
        open={open && !confirming}
        onOpenChange={onOpenChange}
        className="app-modal"
        header={
          <div className={styles.banner}>
            <div className={styles.bannerTitle}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="9" cy="8" r="3.2" />
                <path d="M3.5 20a5.5 5.5 0 0 1 11 0" />
                <path d="M16 6.5a3 3 0 0 1 0 5" />
                <path d="M17.5 14.2A5.5 5.5 0 0 1 20.5 19" />
              </svg>
              新增代理
            </div>
            <div className={styles.bannerSub}>创建代理账号，注册即送 10 元余额</div>
            <div className={styles.bannerBars} aria-hidden="true">
              <i /><i /><i />
            </div>
          </div>
        }
      >
        <div className={styles.body}>
          <button type="button" className={styles.randomBtn} onClick={handleRandom}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M16 3h5v5" />
              <path d="M4 20L21 3" />
              <path d="M21 16v5h-5" />
              <path d="M15 15l6 6" />
              <path d="M4 4l5 5" />
            </svg>
            随机生成
          </button>

          <label className={styles.field}>
            <span className={styles.label}>账号</span>
            <input
              className={styles.input}
              value={username}
              autoComplete="off"
              onChange={(e) => {
                setUsername(e.target.value);
                setError(null);
              }}
            />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>昵称</span>
            <input
              className={styles.input}
              value={realname}
              autoComplete="off"
              onChange={(e) => {
                setRealname(e.target.value);
                setError(null);
              }}
            />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>登录密码</span>
            <input
              className={styles.input}
              value={password}
              autoComplete="off"
              onChange={(e) => {
                setPassword(e.target.value);
                setError(null);
              }}
            />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Telegram（选填）</span>
            <input
              className={styles.input}
              value={telegram}
              autoComplete="off"
              onChange={(e) => {
                setTelegram(e.target.value);
                setError(null);
              }}
            />
          </label>

          {error && <div className={styles.error}>{error}</div>}

          <button
            type="button"
            className={styles.submit}
            disabled={submitting}
            onClick={openConfirm}
          >
            添加代理
          </button>
        </div>
      </Modal>

      {confirming &&
        portalContainer?.current &&
        createPortal(
          <div
            className={styles.sheetOverlay}
            onClick={() => !submitting && setConfirming(false)}
          >
            <div className={styles.sheet} onClick={(e) => e.stopPropagation()}>
              <div className={styles.sheetBanner}>
                <div className={styles.sheetBannerTitle}>确认添加代理</div>
                <div className={styles.sheetBannerSub}>请核对以下基本信息</div>
                <div className={styles.sheetBannerBars} aria-hidden="true">
                  <i /><i /><i />
                </div>
              </div>
              <div className={styles.sheetBody}>
                <div className={styles.sheetRow}>
                  <span className={styles.sheetLabel}>账号</span>
                  <span className={styles.sheetValue}>{username.trim()}</span>
                </div>
                <div className={styles.sheetRow}>
                  <span className={styles.sheetLabel}>昵称</span>
                  <span className={styles.sheetValue}>{realname.trim()}</span>
                </div>
                <div className={styles.sheetRow}>
                  <span className={styles.sheetLabel}>登录密码</span>
                  <span className={styles.sheetValue}>{password.trim()}</span>
                </div>
                <div className={styles.sheetRow}>
                  <span className={styles.sheetLabel}>Telegram</span>
                  <span className={styles.sheetValue}>{telegram.trim() || '—'}</span>
                </div>
                {error && <div className={styles.error}>{error}</div>}
                <div className={styles.sheetActions}>
                  <button
                    type="button"
                    className={styles.sheetCancel}
                    disabled={submitting}
                    onClick={() => setConfirming(false)}
                  >
                    取消
                  </button>
                  <button
                    type="button"
                    className={styles.sheetConfirm}
                    disabled={submitting}
                    onClick={handleSubmit}
                  >
                    {submitting ? '提交中...' : '确认添加'}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          portalContainer.current
        )}

      {created &&
        portalContainer?.current &&
        createPortal(
          <div className={styles.overlay} onClick={handleClose}>
            <div className={styles.resultCard} onClick={(e) => e.stopPropagation()}>
              <div className={styles.resultTitle}>代理创建成功</div>
              <div className={styles.resultDesc}>请妥善保存以下账号密码</div>
              <div className={styles.resultRow}>
                <span className={styles.resultLabel}>账号</span>
                <span className={styles.resultValue}>{created.username}</span>
              </div>
              <div className={styles.resultRow}>
                <span className={styles.resultLabel}>密码</span>
                <span className={styles.resultValue}>{created.password}</span>
              </div>
              <button type="button" className={styles.copyBtn} onClick={handleCopy}>
                {copied ? '已复制' : '复制账号密码'}
              </button>
              <button type="button" className={styles.resultClose} onClick={handleClose}>
                完成
              </button>
            </div>
          </div>,
          portalContainer.current
        )}

      {toast &&
        portalContainer?.current &&
        createPortal(<div className={styles.toast}>{toast}</div>, portalContainer.current)}
    </>
  );
};
